import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Declarant, SortDirection, TransactionTypesCriteria } from '@e-origin/shared';

import { showErrorToast } from '@hooks/showErrorToast';
import { request } from '@utils';

export const defaultAdminDeclarantsCriteria: TransactionTypesCriteria = {
  filters: { searchQuery: '' },
  pagination: { page: 1, size: 10 },
  sorting: { field: 'accountName', direction: SortDirection.ASC },
};

const parseUrlToCriteria = (searchParams: URLSearchParams): TransactionTypesCriteria => {
  const filters: TransactionTypesCriteria['filters'] = {};
  filters.searchQuery = searchParams.get('searchQuery') || '';

  const page = parseInt(searchParams.get('page') || '1', 10);
  const size = parseInt(searchParams.get('size') || '10', 10);

  const sortField = searchParams.get('sortField') || 'accountName';
  const sortDir = (searchParams.get('sortDir') || SortDirection.ASC) as SortDirection;

  return {
    filters,
    pagination: { page, size },
    sorting: { field: sortField, direction: sortDir },
  };
};

const criteriaToSearchParams = (criteria: TransactionTypesCriteria): URLSearchParams => {
  const searchParams = new URLSearchParams();

  if (criteria.filters.searchQuery) {
    searchParams.set('searchQuery', criteria.filters.searchQuery);
  }

  searchParams.set('page', criteria.pagination.page.toString());
  searchParams.set('size', criteria.pagination.size.toString());

  searchParams.set('sortField', criteria.sorting.field);
  searchParams.set('sortDir', criteria.sorting.direction);

  return searchParams;
};

export const getInitialAdminDeclarantsCriteria = (searchParams: URLSearchParams): TransactionTypesCriteria => {
  if (searchParams.toString()) {
    return parseUrlToCriteria(searchParams);
  }
  return defaultAdminDeclarantsCriteria;
};

export const useAdminDeclarantsCriteriaUrlSync = (criteria?: TransactionTypesCriteria) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!criteria) return;

    const currentCriteria = parseUrlToCriteria(searchParams);
    if (searchParams.size && isEqual(currentCriteria, criteria)) return;

    const newParams = criteriaToSearchParams(criteria);
    setSearchParams(newParams, { replace: true });
  }, [criteria]);
};

export const useAdminDeclarants = (criteria: TransactionTypesCriteria = defaultAdminDeclarantsCriteria) => {
  return useQuery({
    queryKey: ['admin-declarants', criteria],
    queryFn: async (): Promise<{ data: { declarants: Declarant[]; totalItems: number } }> => {
      try {
        const response = await request({
          path: 'declarant',
          method: 'POST',
          authenticate: true,
          dataObject: { criteria },
        });
        return response || { data: { declarants: [], totalItems: 0 } };
      } catch (error) {
        showErrorToast(error, 'Failed to fetch declarants list');
        return { data: { declarants: [], totalItems: 0 } };
      }
    },
  });
};
